import React from 'react';
import PropTypes from 'prop-types';
import { map, uniqBy, includes } from 'lodash';
import { compose, withPropsOnChange, withStateHandlers } from 'recompose';
import classnames from 'classnames';

import { vehicleAccommodationOptions } from 'config/units';
import { H3, Paragraph } from 'components/core/Typography';
import { BookableUnitDetails } from 'components/unit/BookableUnitDetails';
import { Dropdown } from 'components/core/Dropdown';

import './VehicleUnits.scss';

export const enhance = compose(
	withStateHandlers(
		{ selectedSize: null, selectedAccommodation: null },
		{
			onSelectSize: () => (selectedSize) => ({ selectedSize }),
			onSelectAccommodation: () => (selectedAccommodation) => ({
				selectedAccommodation,
			}),
		},
	),
	withPropsOnChange(['units'], ({ units }) => ({
		sizes: uniqBy(
			map(units, (unit) => ({
				value: unit.sizeBucket,
				label: `${parseInt(unit.sizeBucket.split(' x ')[0])}′ Long`,
			})),
			'value',
		),
		accommodations: vehicleAccommodationOptions.filter((option) =>
			units.find((unit) =>
				includes(unit.availableAmenities, option.value),
			),
		),
	})),
	withPropsOnChange(['units', 'selectedSize'], ({ selectedSize, units }) => ({
		units: selectedSize
			? units.filter((unit) => unit.sizeBucket === selectedSize)
			: units,
	})),
	withPropsOnChange(
		['units', 'selectedAccommodation'],
		({ selectedAccommodation, units }) => ({
			units: selectedAccommodation
				? units.filter((unit) =>
						includes(
							unit.availableAmenities,
							selectedAccommodation,
						),
					)
				: units,
		}),
	),
);

export function VehicleUnits({
	accommodations,
	expandedUnit,
	facility,
	onCreateBooking,
	onExpandUnit,
	onSelectAccommodation,
	onSelectSize,
	selectedAccommodation,
	selectedSize,
	sizes,
	storageType,
	units,
}) {
	return (
		<div className="vehicle-units">
			<div
				className={classnames('unit-list-header', {
					'show-fees': facility.showFeeWarning,
				})}
			>
				<H3
					size="medium"
					className="headline"
				>
					Available Units
				</H3>
				<div className="unit-list-filters">
					<Dropdown
						label="Unit Sizes"
						options={sizes}
						selected={selectedSize}
						onSelect={onSelectSize}
					/>
					<Dropdown
						label="Accomodations"
						options={accommodations}
						selected={selectedAccommodation}
						onSelect={onSelectAccommodation}
					/>
				</div>
			</div>
			{facility.showFeeWarning && facility.totalFees > 0 && (
				<Paragraph
					last
					className="fees-info-banner"
					color="white"
				>
					Select a unit to see full pricing with additional fees
					displayed.
				</Paragraph>
			)}
			{units.map((unit) => (
				<BookableUnitDetails
					key={unit.id}
					unit={unit}
					facility={facility}
					onExpandUnit={onExpandUnit}
					onCreateBooking={onCreateBooking}
					isExpanded={expandedUnit === unit.id}
					storageType={storageType}
				/>
			))}
			{!units.length && (
				<div className="no-units">
					<Paragraph>
						No unit matched the selected criteria.
					</Paragraph>
				</div>
			)}
		</div>
	);
}

VehicleUnits.propTypes = {
	accommodations: PropTypes.arrayOf(PropTypes.object).isRequired,
	expandedUnit: PropTypes.number,
	facility: PropTypes.object.isRequired,
	onCreateBooking: PropTypes.func.isRequired,
	onExpandUnit: PropTypes.func.isRequired,
	onSelectAccommodation: PropTypes.func.isRequired,
	onSelectSize: PropTypes.func.isRequired,
	selectedAccommodation: PropTypes.string,
	selectedSize: PropTypes.string,
	sizes: PropTypes.arrayOf(PropTypes.object).isRequired,
	storageType: PropTypes.string.isRequired,
	units: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default enhance(VehicleUnits);
